import Button from "@common/buttons/button"
import useDebouncedCallback from "@common/hooks/useDebounceCallback"
import { isEmpty, isNullOrEmpty, isObjectsEqual } from "@common/lib/util"
import MenuStyled from "@common/menuStyled"
import SearchBar from "@common/searchBar"
import ShowHideColumnMenu from "@common/table/menus/showHideColumnMenu"
import { TableHeadToolbarProps } from "@common/table/types"
import { getEditableTableState } from "@common/table/util"
import Tag from "@common/tags/tag"
import { Action } from "@common/types"
import React, { useState } from "react"

const TableHeadToolbar = ({
  table,
  commonActions,
  onSearch,
  multiSelectActions,
  onMultiSelectActionClick,
}: TableHeadToolbarProps) => {
  const {
    options: {
      enableColumnVisibility,
      enableGlobalFilter,
      manualSearch,
      enableRowSelection,
      showToolbar
    },
    setGlobalFilter,
    refs: { searchInputRef },
    getSelectedRowModel,
    initialState,
    getState,
  } = table
  const state = getState()
  const tableState = getEditableTableState(state)

  const isTableStateChanged = !isObjectsEqual(
    tableState,
    getEditableTableState(initialState)
  )

  const debounceSearch = useDebouncedCallback(onSearch, 500)
  const handleSearch = (text: string) => {
    searchInputRef.current = text
    if (manualSearch) {
      debounceSearch?.(text)
    } else setGlobalFilter(text)
  }
  const selectedRowIds = enableRowSelection
    ? getSelectedRowModel().flatRows.map((row) => row.original.id)
    : []

  // const hasToolbarLeftItems = enableGlobalFilter
  const hasToolbarRightItems =
    !isNullOrEmpty(multiSelectActions) ||
    !isNullOrEmpty(commonActions) ||
    enableColumnVisibility
  const showSaveButton = enableColumnVisibility
  const hasToolbarItems =
    enableGlobalFilter ||
    hasToolbarRightItems ||
    enableColumnVisibility ||
    (isTableStateChanged && showSaveButton)

  if (hasToolbarItems)
    return (
      <div
        className="flex justify-between p-2 border-b"
        ref={table.refs.topToolbarRef}
      >
        {enableGlobalFilter ? (
          <div className="border  rounded-md px-2">
            <SearchBar
              onChange={handleSearch}
              onClearInput={() => setGlobalFilter(undefined)}
            />
          </div>
        ) : (
          <div></div>
        )}

        {hasToolbarRightItems && (
          <div className="flex items-center gap-2 mx-6">
            <MultiSelectActions
              {...{ multiSelectActions, selectedRowIds, onMultiSelectActionClick }}
            />
            {selectedRowIds.length > 0 && (
              <Tag color="gray">
                {selectedRowIds.length} <span className="px-2">Selected</span>
              </Tag>
            )}
            <CommonActions commonActions={commonActions} />
            {enableColumnVisibility && showToolbar && <ShowHideColumnMenu table={table} />}
          </div>
        )}
      </div>
    )
  return <></>
}

function CommonActions({
  commonActions,
}: Pick<TableHeadToolbarProps, "commonActions">) {
  return (
    <>
      {commonActions?.map((action) => (
        <div key={action.key}>{action.display}</div>
      ))}
    </>
  )
}

function MultiSelectActions({
  multiSelectActions,
  selectedRowIds,
  onMultiSelectActionClick,
}: Pick<TableHeadToolbarProps, "multiSelectActions" | "onMultiSelectActionClick"> & {
  selectedRowIds: Array<string>
}) {
  const [isRefreshed, setIsRefreshed] = useState(false) // special handling for refresh button

  if (isNullOrEmpty(multiSelectActions) || (isEmpty(selectedRowIds) && !isRefreshed))
    return <></>

  const handleOptionClick = (action: Action) => {
    setIsRefreshed(!isRefreshed)
    onMultiSelectActionClick?.(action, selectedRowIds)
  }

  // special handling for refresh button //TODO: This hardcoded should be handled in frontend
  const actions =
    isRefreshed && !selectedRowIds.length
      ? multiSelectActions?.filter((action) => action.key === "refresh")
      : multiSelectActions

  return (
    <MenuStyled
      buttonTextOverride="Actions"
      options={actions}
      optionWidth="w-52"
      onOptionClick={handleOptionClick}
      buttonOverride={undefined}
      position={undefined}
    />
  )
}

export default TableHeadToolbar
